/**
 * Vincent #113/#114/#115 (Sprint 4) — Panneau Canaux complet.
 * Architecture :
 *  - Sidebar gauche : liste des canaux avec emoji + nom + unread badge.
 *  - Zone centrale : messages du canal sélectionné + input.
 *  - Header canal : nom + emoji + case "Indexé par chatbot" (admin).
 *  - Pour Conseillers/Stagiaires : toggle par message (Vincent #115).
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import {
  Hash,
  Plus,
  PaperPlaneTilt,
  Robot,
  Pencil,
  Trash,
  X,
  CheckCircle,
  CircleNotch,
  ChartBar,
  MagnifyingGlass,
  Smiley,
  At,
  CaretDown,
  SpeakerHigh,
  SpeakerSlash,
} from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LanguageContext";
import { canEditContent } from "../../utils/permissions";

import { API_BASE as API } from "../../lib/apiBase";
import { useSmartScrollAndNewMessages } from "../../hooks/useSmartScrollAndNewMessages";
import { isSoundEnabled, setSoundEnabled, playDing, syncSoundFromServer } from "../../lib/messagingSound";
import PollCard from "./PollCard";
import { texteMessage } from "../../lib/texteMessage";
import PollComposer from "./PollComposer";
import PinnedPoll from "./PinnedPoll";
import { usePollPermission } from "../../hooks/usePollPermission";

function formatTime(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return d.toLocaleTimeString(lang === "fr" ? "fr-CA" : "en-CA", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function ChannelsPanel() {
  const { user } = useAuth();
  const { lang } = useLang();
  // Eric 2026-06-25 (iter117 B2) — migré vers canEditContent (helper canonique,
  // aligné backend require_editor). Auparavant : `is_admin || is_founder ||
  // role ∈ {admin, founder, concepteur}` — équivalent fonctionnel mais incorrect
  // pour ceo/president/vp/employee_advanced/access_level=avance.
  const isAdmin = canEditContent(user) || !!user?.is_admin;

  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  // Vincent #130 (Eric 2026-05-13) — Mobile : la liste des canaux devient un
  // menu déroulant compact pour libérer la place à la zone messages.
  const [mobileChannelsOpen, setMobileChannelsOpen] = useState(false);
  // Phase 4.6 (2026-05-12) — Recherche + mentions.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [mentionsUnread, setMentionsUnread] = useState(0);
  // Eric 2026-07-30 (B596 enhancement) — Toggle son ding, miroir localStorage.
  const [soundOn, setSoundOn] = useState(() => isSoundEnabled());
  // Tâche 1424 — Sondages. Le droit de créer est coché PAR PERSONNE par un
  // admin (Messagerie › Accès utilisateurs) ; les rôles qui éditent le
  // contenu l'ont toujours.
  const { canCreatePoll } = usePollPermission();
  const [showPollComposer, setShowPollComposer] = useState(false);

  // Eric 2026-07-30 (B596.d) — Hydrate depuis les préférences serveur
  // (`user.preferences.messaging_sound_enabled`). Cross-device sync.
  useEffect(() => {
    const changed = syncSoundFromServer(user?.preferences);
    if (changed) setSoundOn(isSoundEnabled());
  }, [user?.preferences]);
  const pollingRef = useRef(null);
  // Vincent B596.d (Eric 2026-07-28) — Logique scroll/counter/pulse factorisée
  const {
    messagesContainerRef,
    messagesEndRef,
    onMessagesScroll,
    newMsgCount,
    pulse,
    scrollToLatest,
    markJustSent,
  } = useSmartScrollAndNewMessages(messages, activeChannel?.id);

  const loadChannels = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/channels`, { withCredentials: true });
      setChannels(data);
      // Eric (2026-05-18) — Bug "le canal revient toujours sur Info général"
      // ----------------------------------------------------------------
      // Avant : `if (!activeChannel && data.length > 0) setActiveChannel(data[0])`
      // Mais loadChannels est dans un useCallback([]) — donc la closure capture
      // activeChannel = null POUR TOUJOURS. Toutes les 8s le polling rappelait
      // loadChannels() qui voyait toujours activeChannel === null et resettait
      // le canal actif sur le premier (Info général).
      // Fix : functional update — on consulte la VRAIE valeur courante.
      setActiveChannel((prev) => prev || (data.length > 0 ? data[0] : null));
    } catch (e) {
      console.error("loadChannels:", e);
    }
  }, []);

  const loadMessages = useCallback(async (channelId, silent = false) => {
    if (!channelId) return;
    if (!silent) setLoadingMsg(true);
    try {
      const { data } = await axios.get(`${API}/api/channels/${channelId}/messages`, { withCredentials: true });
      setMessages(data.messages || []);
      // refresh channel meta (indexed_by_chatbot etc)
      if (data.channel) setActiveChannel((cur) => (cur?.id === data.channel.id ? { ...cur, ...data.channel } : cur));
    } catch (e) {
      console.error("loadMessages:", e);
    }
    setLoadingMsg(false);
  }, []);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  // Vincent #131 (Eric 2026-05-13) — Rafraîchir la liste des canaux (et donc
  // les pastilles de messages non-lus) toutes les 8 secondes pour que la
  // notification apparaisse même sans naviguer sur le canal concerné.
  useEffect(() => {
    const id = setInterval(() => { loadChannels(); }, 8000);
    return () => clearInterval(id);
  }, [loadChannels]);

  // Phase 4.6 — Load mentions counter every 30s.
  useEffect(() => {
    const loadMentions = async () => {
      try {
        const { data } = await axios.get(`${API}/api/channels/mentions`, { withCredentials: true });
        const unread = (data.mentions || []).filter(m => !m.read_by_me).length;
        setMentionsUnread(unread);
      } catch (e) { /* silent — non-critical */ }
    };
    loadMentions();
    const id = setInterval(loadMentions, 30000);
    return () => clearInterval(id);
  }, []);

  // Phase 4.6 — Search messages with debounce.
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/channels/search`, { params: { q: searchQuery }, withCredentials: true });
        setSearchResults(data);
      } catch (e) { console.error(e); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const toggleReaction = async (msg, emoji) => {
    try {
      const { data } = await axios.post(`${API}/api/channels/messages/${msg.id}/react`, { emoji }, { withCredentials: true });
      setMessages(cur => cur.map(m => m.id === msg.id ? { ...m, reactions: data.reactions } : m));
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    }
  };

  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (activeChannel?.id) {
      loadMessages(activeChannel.id);
      pollingRef.current = setInterval(() => loadMessages(activeChannel.id, true), 4000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [activeChannel?.id, loadMessages]);

  // Bug 539 (Eric 2026-07-24) + B596.b/c/d (2026-07-28) — La logique de scroll
  // intelligent + bouton flottant « ↓ Nouveaux messages » est désormais dans
  // le hook `useSmartScrollAndNewMessages` (partagé avec AgoraPage).

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeChannel || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/api/channels/${activeChannel.id}/messages`,
        { text: newMsg.trim() },
        { withCredentials: true }
      );
      setMessages((prev) => [...prev, data]);
      // Bug 539 — on marque que le user vient d'envoyer, pour forcer le
      // scroll auto (contrairement à un message reçu passivement).
      markJustSent();
      setNewMsg("");
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur d'envoi" : "Send error");
    }
    setSending(false);
  };

  const toggleChannelIndex = async () => {
    if (!activeChannel || !isAdmin) return;
    try {
      const { data } = await axios.patch(
        `${API}/api/channels/${activeChannel.id}`,
        { indexed_by_chatbot: !activeChannel.indexed_by_chatbot },
        { withCredentials: true }
      );
      setActiveChannel(data);
      setChannels((cur) => cur.map((c) => (c.id === data.id ? { ...c, indexed_by_chatbot: data.indexed_by_chatbot } : c)));
      toast.success(
        data.indexed_by_chatbot
          ? (lang === "fr" ? "Canal indexé par le chatbot ✓" : "Channel indexed ✓")
          : (lang === "fr" ? "Indexation désactivée" : "Indexing disabled")
      );
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    }
  };

  const toggleMessageIndex = async (msg, include) => {
    try {
      const { data } = await axios.patch(
        `${API}/api/channels/messages/${msg.id}/index`,
        { include },
        { withCredentials: true }
      );
      setMessages((cur) => cur.map((m) => (m.id === msg.id ? { ...m, ...data } : m)));
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    }
  };

  const deleteChannel = async () => {
    if (!activeChannel || !isAdmin) return;
    const ok = await confirmDialog(lang === "fr"
      ? `Supprimer définitivement le canal « ${activeChannel.name} » et tous ses messages ?`
      : `Permanently delete channel "${activeChannel.name}" and all its messages?`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/channels/${activeChannel.id}`, { withCredentials: true });
      toast.success(lang === "fr" ? "Canal supprimé" : "Channel deleted");
      setActiveChannel(null);
      await loadChannels();
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    }
  };

  return (
    // Bug 542 v3 (Eric 2026-07-24) — Comme AgoraPage, applique un
    // sizing explicite sur ce panneau car le parent MessagingPage n'a
    // pas de hauteur bornée depuis le DashboardLayout. Sans ça, la zone
    // input « Écrire un message » débordait la fenêtre → browser scroll
    // masquait les tabs.
    // Vincent B548 v3 (Eric 2026-07-24) — voir MessagingPage.js pour le détail.
    // On utilise `h-full` : la hauteur est bornée par le parent.
    <div className="h-full flex flex-col sm:flex-row">
      {/* Vincent #130 (Eric 2026-05-13) — Mobile only : barre dropdown compacte
          au-dessus de la zone messages, qui révèle la liste des canaux à la
          demande. Cache la sidebar pleine hauteur en dessous de sm. */}
      <div className="sm:hidden border-b border-zinc-200 bg-zinc-50/50" data-testid="channels-mobile-header">
        <button
          type="button"
          onClick={() => setMobileChannelsOpen((p) => !p)}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-zinc-900"
          data-testid="channels-mobile-toggle"
          aria-expanded={mobileChannelsOpen}
        >
          <Hash size={16} weight="bold" />
          <span className="flex-1 text-left truncate">
            {activeChannel ? (
              <>
                <span className="mr-1">{activeChannel.emoji}</span>{activeChannel.name}
              </>
            ) : (
              lang === "fr" ? "Choisir un canal" : "Pick a channel"
            )}
          </span>
          {/* Mention badge (total unread channels) */}
          {mentionsUnread > 0 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold" title={`${mentionsUnread} mention(s)`}>
              <At size={9} weight="bold" />{mentionsUnread}
            </span>
          )}
          <CaretDown size={14} weight="bold" className={`transition-transform ${mobileChannelsOpen ? "rotate-180" : ""}`} />
        </button>
        {mobileChannelsOpen && (
          <ul className="max-h-[40vh] overflow-y-auto border-t border-zinc-200 bg-white" data-testid="channels-mobile-list">
            {channels.map((c, idx) => {
              const isActive = activeChannel?.id === c.id;
              const channelNum = idx + 1;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => { setActiveChannel(c); setMobileChannelsOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                      isActive ? "bg-[#002FA7]/8 text-[#002FA7] font-medium" : "text-zinc-700 hover:bg-zinc-100"
                    }`}
                    data-testid={`channel-mobile-item-${c.slug}`}
                  >
                    <span className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1 rounded text-[10px] font-mono font-bold bg-zinc-200 text-zinc-700 shrink-0">
                      #{channelNum}
                    </span>
                    <span className="text-base">{c.emoji}</span>
                    <span className="truncate flex-1">{c.name}</span>
                    {c.unread > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-teal-500 text-white text-[10px] font-bold flex-shrink-0">{c.unread}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Sidebar canaux (desktop only ≥ sm) ── */}
      <aside className="hidden sm:flex w-64 sm:w-72 shrink-0 border-r border-zinc-200 bg-zinc-50/50 flex-col" data-testid="channels-sidebar">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="text-sm font-bold text-zinc-900 inline-flex items-center gap-2">
            <Hash size={16} weight="bold" />
            {lang === "fr" ? "Canaux" : "Channels"}
            {mentionsUnread > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold" title={`${mentionsUnread} mention(s)`} data-testid="mentions-badge">
                <At size={9} weight="bold" />{mentionsUnread}
              </span>
            )}
          </h2>
          {isAdmin && (
            <button
              onClick={() => setShowCreate(true)}
              className="p-1.5 rounded hover:bg-zinc-200 text-zinc-600"
              title={lang === "fr" ? "Créer un canal" : "Create channel"}
              data-testid="create-channel-btn"
            >
              <Plus size={15} weight="bold" />
            </button>
          )}
        </div>
        {/* Phase 4.6 — Recherche messages */}
        <div className="px-3 py-2 border-b border-zinc-200 bg-white">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-2 top-2.5 text-zinc-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={lang === "fr" ? "Rechercher…" : "Search…"}
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-zinc-200 rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7]/40"
              data-testid="channels-search-input"
            />
          </div>
        </div>
        <ul className="flex-1 overflow-y-auto py-1">
          {channels.map((c, idx) => {
            const isActive = activeChannel?.id === c.id;
            // Vincent #153 — Numéro unique de canal préfixé au nom pour les
            // distinguer même si l'utilisateur a créé deux canaux portant le
            // même nom. On utilise l'index dans le tableau (les canaux étant
            // triés par created_at, ce numéro est stable et cohérent entre
            // les sessions tant que l'ordre ne change pas).
            const channelNum = idx + 1;
            return (
              <li key={c.id}>
                <button
                  onClick={() => setActiveChannel(c)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
                    isActive ? "bg-[#002FA7]/8 text-[#002FA7] font-medium" : "text-zinc-700 hover:bg-zinc-100"
                  }`}
                  data-testid={`channel-item-${c.slug}`}
                >
                  {/* Vincent #176 (Eric 2026-05-27 round 5) — Numéro RETIRÉ
                      de la gauche, déplacé tout à DROITE après l'icône
                      d'indexation chatbot, en texte gris discret (pas en badge). */}
                  <span className="text-base">{c.emoji}</span>
                  <span className="truncate flex-1">{c.name}</span>
                  {/* Vincent #131 (Eric 2026-05-13) — Pastille verte (cohérent avec DM)
                      pour les messages non-lus du canal. Polling toutes les 8s. */}
                  {c.unread > 0 && (
                    <span
                      className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-teal-500 text-white text-[10px] font-bold flex-shrink-0"
                      data-testid={`channel-unread-badge-${c.slug}`}
                      title={`${c.unread} message(s) non lu(s)`}
                    >
                      {c.unread}
                    </span>
                  )}
                  {c.indexed_by_chatbot && (
                    <Robot size={11} weight="bold" className="text-teal-600 shrink-0" title="Indexé" />
                  )}
                  {/* Vincent #176 (Eric 2026-05-27) — Numéro discret à droite */}
                  <span
                    className="text-[10px] font-mono text-zinc-400 shrink-0 tabular-nums"
                    data-testid={`channel-number-${c.slug}`}
                    title={`Canal n°${channelNum}`}
                  >
                    #{channelNum}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* ── Zone messages ── */}
      <main className="flex-1 min-w-0 flex flex-col bg-white">
        {!activeChannel ? (
          <div className="flex-1 flex items-center justify-center text-zinc-400 text-sm">
            {lang === "fr" ? "Sélectionnez un canal" : "Select a channel"}
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between" data-testid="active-channel-header">
              <div className="min-w-0">
                <h3 className="font-bold text-base inline-flex items-center gap-2">
                  {/* Vincent #153 — Affiche aussi le numéro #N en header pour confirmer
                      visuellement le canal actuel. Aide quand 2 canaux ont des noms similaires. */}
                  {(() => {
                    const idx = channels.findIndex(ch => ch.id === activeChannel.id);
                    return idx >= 0 ? (
                      <span className="inline-flex items-center justify-center min-w-[26px] h-[20px] px-1.5 rounded text-[11px] font-mono font-bold bg-zinc-200 text-zinc-700 shrink-0" data-testid="active-channel-number">
                        #{idx + 1}
                      </span>
                    ) : null;
                  })()}
                  <span>{activeChannel.emoji}</span>
                  <span data-testid="active-channel-name">{activeChannel.name}</span>
                </h3>
                {activeChannel.description && <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{activeChannel.description}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Eric 2026-07-30 (B596 enhancement) — Toggle son ding
                    discret sur nouveau message. Muet par défaut. Visible
                    pour TOUS les rôles (le son est une préférence perso). */}
                <button
                  type="button"
                  onClick={() => {
                    const next = !soundOn;
                    setSoundOn(next);
                    setSoundEnabled(next);
                    if (next) playDing();
                  }}
                  className={`p-1.5 rounded hover:bg-zinc-100 transition-colors ${soundOn ? "text-[#002FA7]" : "text-zinc-500"}`}
                  data-testid="channels-sound-toggle"
                  title={soundOn ? (lang === "fr" ? "Son activé — Cliquez pour muter" : "Sound on — Click to mute") : (lang === "fr" ? "Son muet — Cliquez pour activer" : "Muted — Click to enable sound")}
                  aria-label={soundOn ? "Son activé" : "Son muet"}
                  aria-pressed={soundOn}
                >
                  {soundOn ? <SpeakerHigh size={14} /> : <SpeakerSlash size={14} />}
                </button>
                {isAdmin && (<>
                  <label className="inline-flex items-center gap-1.5 text-[11px] text-zinc-700 cursor-pointer" data-testid="channel-index-toggle-label">
                    <input
                      type="checkbox"
                      checked={!!activeChannel.indexed_by_chatbot}
                      onChange={toggleChannelIndex}
                      className="w-3.5 h-3.5 accent-[#002FA7]"
                      data-testid="channel-index-toggle"
                    />
                    <Robot size={12} weight="bold" />
                    {lang === "fr" ? "Indexer par chatbot" : "Index by chatbot"}
                  </label>
                  <button
                    onClick={() => setShowEdit(true)}
                    className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
                    title={lang === "fr" ? "Renommer" : "Rename"}
                    data-testid="edit-channel-btn"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={deleteChannel}
                    className="p-1.5 rounded hover:bg-red-50 text-red-500"
                    title={lang === "fr" ? "Supprimer" : "Delete"}
                    data-testid="delete-channel-btn"
                  >
                    <Trash size={13} />
                  </button>
                </>)}
              </div>
            </header>

            {/* Tâche 1425 — Posé AVANT la zone défilante : « épinglé »
                veut dire qu'il ne part pas avec les messages. */}
            {activeChannel && (
              <PinnedPoll
                channelId={activeChannel.id}
                currentUserId={user?.id}
                canAdmin={isAdmin}
                cle={messages.length}
              />
            )}
            {/* Messages */}
            <div className="flex-1 relative min-h-0">
              <div
                ref={messagesContainerRef}
                onScroll={onMessagesScroll}
                className="absolute inset-0 overflow-y-auto px-5 py-4 space-y-3"
                data-testid="channel-messages"
              >
              {loadingMsg ? (
                <div className="text-center text-zinc-400 text-sm pt-8">
                  <CircleNotch size={20} className="mx-auto animate-spin mb-2" />
                  {lang === "fr" ? "Chargement…" : "Loading…"}
                </div>
              ) : messages.length === 0 ? (
                <p className="text-center text-zinc-400 text-sm pt-8">
                  {lang === "fr" ? "Soyez le premier à écrire dans ce canal !" : "Be the first to post in this channel!"}
                </p>
              ) : (
                messages.map((m) => {
                  const isMe = m.sender_id === user?.id;
                  const canToggle = m.sender_id === user?.id || isAdmin;
                  const canDelete = m.sender_id === user?.id || isAdmin;
                  // Vincent #135 round 2 (Eric 2026-05-16) — Bouton supprimer mis
                  // en évidence (icône Trash rouge claire visible au survol du
                  // message). Auparavant un petit 🗑 collé au "X" indexation
                  // créait la confusion : Vincent cliquait sur le X (= exclure
                  // du chatbot) en pensant supprimer le message.
                  const handleDeleteMessage = async () => {
                    const ok = await confirmDialog(lang === "fr"
                      ? "Supprimer ce message ? Il sera masqué pour tous les participants et retiré de l'indexation du chatbot (Loi 25 : trace audit conservée en DB)."
                      : "Delete this message? It will be hidden for all participants and removed from chatbot indexing.");
                    if (!ok) return;
                    try {
                      await axios.delete(`${API}/api/channels/messages/${m.id}`, { withCredentials: true });
                      setMessages((cur) => cur.filter((x) => x.id !== m.id));
                      toast.success(lang === "fr" ? "Message supprimé" : "Message deleted");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur lors de la suppression" : "Delete failed"));
                    }
                  };
                  return (
                    <div key={m.id} className={`group flex gap-3 ${isMe ? "flex-row-reverse" : ""}`} data-testid={`message-${m.id}`}>
                      <div className="w-8 h-8 rounded-full bg-[#002FA7]/15 text-[#002FA7] flex items-center justify-center text-xs font-bold shrink-0">
                        {(m.sender_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className={`${m.kind === "poll" ? "max-w-[88%] w-full" : "max-w-[70%]"} ${isMe ? "items-end" : "items-start"} flex flex-col gap-1`}>
                        <div className="flex items-baseline gap-2 text-[11px] text-zinc-500">
                          <span className="font-medium text-zinc-700">{m.sender_name}</span>
                          {m.sender_role && <span className="text-zinc-400">{m.sender_role}</span>}
                          <span>{formatTime(m.created_at, lang)}</span>
                          {/* Vincent #135 round 3 (Eric 2026-05-18) — Bouton Supprimer
                              TOUJOURS visible (plus de opacity-0 group-hover) car
                              Vincent ne savait pas qu'il fallait survoler le message
                              pour le voir, et cliquait sur le "X" de l'indexation
                              en pensant supprimer. */}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={handleDeleteMessage}
                              className="inline-flex items-center gap-1 text-rose-600 hover:text-white hover:bg-rose-600 px-1.5 py-0.5 rounded transition-colors text-[10px] font-semibold border border-rose-300 hover:border-rose-600 bg-white"
                              title={lang === "fr" ? "Supprimer définitivement ce message pour tous les participants" : "Permanently delete this message for all participants"}
                              data-testid={`msg-delete-${m.id}`}
                            >
                              <Trash size={10} weight="bold" />
                              <span>{lang === "fr" ? "Supprimer" : "Delete"}</span>
                            </button>
                          )}
                        </div>
                        {/* Tâche 1424 — Un sondage REMPLACE la bulle de texte
                            plutôt que de s'y ajouter : la question est déjà
                            dans la carte, la redire au-dessus donnerait à
                            lire deux fois la même phrase. */}
                        {m.kind === "poll" ? (
                          <PollCard
                            message={m}
                            currentUserId={user?.id}
                            canAdmin={isAdmin}
                            onChanged={(maj) => setMessages((cur) => cur.map((x) => (x.id === maj.id ? { ...x, ...maj } : x)))}
                          />
                        ) : (
                          <div className={`px-3 py-2 rounded-lg text-sm ${isMe ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-900"}`}>
                            {(texteMessage(m, lang) || "").split(/(@\w+)/g).map((part, i) =>
                              part.startsWith("@")
                                ? <span key={i} className={isMe ? "text-blue-200 font-semibold" : "text-[#002FA7] font-semibold"}>{part}</span>
                                : <span key={i}>{part}</span>
                            )}
                          </div>
                        )}
                        {/* Phase 4.6 (2026-05-12) — Emoji reactions row */}
                        <div className="flex items-center gap-1 flex-wrap" data-testid={`reactions-row-${m.id}`}>
                          {Object.entries(m.reactions || {}).map(([emoji, users]) => {
                            const me = users.includes(user?.id);
                            return (
                              <button
                                key={emoji}
                                onClick={() => toggleReaction(m, emoji)}
                                className={`text-[11px] px-1.5 py-0.5 rounded-full border ${me ? "bg-[#002FA7]/10 border-[#002FA7]/40 text-[#002FA7] font-semibold" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                                title={users.length === 1 ? "1 personne" : `${users.length} personnes`}
                                data-testid={`reaction-${emoji}-${m.id}`}
                              >
                                {emoji} {users.length}
                              </button>
                            );
                          })}
                          <div className="relative group">
                            <button
                              className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-[#002FA7] text-[11px] p-0.5"
                              data-testid={`add-reaction-${m.id}`}
                              title={lang === "fr" ? "Ajouter une réaction" : "Add reaction"}
                            >
                              <Smiley size={13} />
                            </button>
                            <div className="hidden group-hover:flex absolute bottom-full left-0 bg-white border border-zinc-200 rounded-lg shadow-lg p-1 gap-0.5 z-10">
                              {["👍", "❤️", "🎉", "😄", "🤔", "👀"].map(e => (
                                <button key={e} onClick={() => toggleReaction(m, e)} className="hover:bg-zinc-100 rounded px-1 text-sm" data-testid={`emoji-picker-${e}-${m.id}`}>{e}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                        {/* Vincent #115 — Toggle per-message indexation (sender or admin) */}
                        {canToggle && (
                          <div className="inline-flex items-center gap-1 text-[10px] text-zinc-400" data-testid={`msg-index-controls-${m.id}`}>
                            <Robot size={10} weight="bold" />
                            <span>{lang === "fr" ? "Indexation :" : "Index:"}</span>
                            <button
                              onClick={() => toggleMessageIndex(m, true)}
                              className={`px-1.5 rounded ${m.index_override === true ? "bg-teal-100 text-teal-800 font-bold" : "hover:bg-zinc-100"}`}
                              title={lang === "fr" ? "Forcer indexer" : "Force include"}
                              data-testid={`msg-index-include-${m.id}`}
                            >
                              <CheckCircle size={11} weight="bold" />
                            </button>
                            <button
                              onClick={() => toggleMessageIndex(m, false)}
                              className={`px-1.5 rounded ${m.index_override === false ? "bg-red-100 text-red-800 font-bold" : "hover:bg-zinc-100"}`}
                              title={lang === "fr" ? "Forcer exclure" : "Force exclude"}
                              data-testid={`msg-index-exclude-${m.id}`}
                            >
                              <X size={11} weight="bold" />
                            </button>
                            <button
                              onClick={() => toggleMessageIndex(m, null)}
                              className={`px-1.5 rounded ${m.index_override == null ? "bg-zinc-100 text-zinc-700 font-bold" : "hover:bg-zinc-100"}`}
                              title={lang === "fr" ? "Suivre le canal (défaut)" : "Follow channel (default)"}
                              data-testid={`msg-index-default-${m.id}`}
                            >
                              =
                            </button>
                            <span className="ml-1 italic">
                              {m.indexed_by_chatbot ? (lang === "fr" ? "indexé" : "indexed") : (lang === "fr" ? "non indexé" : "not indexed")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
              </div>
              {/* Vincent B596.b/.c mirror (Eric 2026-07-28) — Bouton flottant «↓ Nouveaux messages» */}
              {newMsgCount > 0 && (
                <button
                  type="button"
                  onClick={scrollToLatest}
                  className={`absolute bottom-3 right-4 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#002FA7] hover:bg-[#002280] text-white text-xs font-semibold shadow-lg transition-colors ${pulse ? "animate-bounce" : ""}`}
                  data-testid="channel-new-msgs-btn"
                  aria-label={lang === "fr" ? `${newMsgCount} nouveau(x) message(s)` : `${newMsgCount} new message(s)`}
                >
                  <span>↓</span>
                  <span data-testid="channel-new-msgs-count">{newMsgCount}</span>
                  <span>{lang === "fr" ? (newMsgCount > 1 ? "nouveaux messages" : "nouveau message") : (newMsgCount > 1 ? "new messages" : "new message")}</span>
                </button>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-zinc-200 p-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMsg}
                  onChange={(e) => setNewMsg(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder={`${lang === "fr" ? "Message" : "Message"} #${activeChannel.name}`}
                  className="flex-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/40"
                  data-testid="channel-message-input"
                />
                {canCreatePoll && (
                  <button
                    type="button"
                    onClick={() => setShowPollComposer(true)}
                    className="px-2.5 py-2 rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 inline-flex items-center gap-1.5 text-sm font-semibold"
                    data-testid="channel-poll-btn"
                    title={lang === "fr" ? "Créer un sondage dans ce canal" : "Create a poll in this channel"}
                  >
                    <ChartBar size={14} weight="bold" />
                    <span className="hidden sm:inline">{lang === "fr" ? "Sondage" : "Poll"}</span>
                  </button>
                )}
                <button
                  onClick={sendMessage}
                  disabled={!newMsg.trim() || sending}
                  className="px-3 py-2 rounded-lg bg-[#002FA7] text-white hover:bg-[#001F7A] disabled:opacity-40 inline-flex items-center gap-1.5"
                  data-testid="channel-send-btn"
                >
                  {sending ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* ── Tâche 1424 — Fenêtre de création d'un sondage ── */}
      {showPollComposer && activeChannel && (
        <PollComposer
          channelId={activeChannel.id}
          onClose={() => setShowPollComposer(false)}
          onCreated={(sondage) => setMessages((cur) => [...cur, sondage])}
        />
      )}

      {/* ── Modal Create Channel ── */}
      {showCreate && (
        <ChannelEditModal
          mode="create"
          lang={lang}
          onClose={() => setShowCreate(false)}
          onSaved={(c) => { setShowCreate(false); loadChannels(); setActiveChannel(c); }}
        />
      )}
      {showEdit && activeChannel && (
        <ChannelEditModal
          mode="edit"
          channel={activeChannel}
          lang={lang}
          onClose={() => setShowEdit(false)}
          onSaved={(c) => { setShowEdit(false); setActiveChannel(c); loadChannels(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Modal create/edit channel
// ─────────────────────────────────────────────────────────────
function ChannelEditModal({ mode, channel, onClose, onSaved, lang }) {
  const [name, setName] = useState(channel?.name || "");
  const [emoji, setEmoji] = useState(channel?.emoji || "💬");
  const [description, setDescription] = useState(channel?.description || "");
  const [indexed, setIndexed] = useState(!!channel?.indexed_by_chatbot);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (mode === "create") {
        const { data } = await axios.post(`${API}/api/channels`, {
          name: name.trim(), emoji, description, indexed_by_chatbot: indexed,
        }, { withCredentials: true });
        onSaved(data);
        toast.success(lang === "fr" ? "Canal créé ✓" : "Channel created ✓");
      } else {
        const { data } = await axios.patch(`${API}/api/channels/${channel.id}`, {
          name: name.trim(), emoji, description, indexed_by_chatbot: indexed,
        }, { withCredentials: true });
        onSaved(data);
        toast.success(lang === "fr" ? "Canal modifié ✓" : "Channel updated ✓");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="channel-edit-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-bold text-base">{mode === "create" ? (lang === "fr" ? "Nouveau canal" : "New channel") : (lang === "fr" ? "Modifier le canal" : "Edit channel")}</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase text-zinc-500 mb-1">{lang === "fr" ? "Emoji" : "Emoji"}</label>
            <input type="text" maxLength={4} value={emoji} onChange={(e) => setEmoji(e.target.value)} className="w-16 px-2 py-1.5 border border-zinc-300 rounded text-center text-lg" data-testid="modal-channel-emoji" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase text-zinc-500 mb-1">{lang === "fr" ? "Nom" : "Name"}</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="modal-channel-name" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase text-zinc-500 mb-1">{lang === "fr" ? "Description" : "Description"}</label>
            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="modal-channel-description" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={indexed} onChange={(e) => setIndexed(e.target.checked)} className="w-4 h-4 accent-[#002FA7]" data-testid="modal-channel-indexed" />
            <Robot size={14} weight="bold" />
            {lang === "fr" ? "Indexer par le chatbot" : "Index by chatbot"}
          </label>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 rounded">{lang === "fr" ? "Annuler" : "Cancel"}</button>
          <button onClick={save} disabled={saving || !name.trim()} className="px-3 py-1.5 text-sm font-semibold bg-[#002FA7] text-white rounded disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="modal-channel-save">
            {saving && <CircleNotch size={12} className="animate-spin" />}
            {lang === "fr" ? "Enregistrer" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
